import { COUNTRIES } from '@/pwa/constants'

export type SupportedCountryLanguage = 'cs' | 'en' | 'de' | 'pl'
export type CountryGroupId = 'frequent' | 'europe' | 'world'

export type InvoiceCountryOption = {
    isoCode: string
    name: string
    aliases: string[]
    groupId: CountryGroupId
    groupLabel: string
    searchValue: string
}

export type InvoiceCountryGroup = {
    id: CountryGroupId
    label: string
    options: InvoiceCountryOption[]
}

const UNSUPPORTED_BACKEND_COUNTRIES = new Set(['XK'])
const FLAG_FALLBACK = '🌐'

export const PRIORITY_COUNTRIES = ['CZ', 'DE', 'PL', 'SK'] as const

export const EUROPEAN_COUNTRIES = new Set([
    'AD', 'AL', 'AT', 'AX', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES',
    'FI', 'FO', 'FR', 'GB', 'GG', 'GI', 'GR', 'HR', 'HU', 'IE', 'IM', 'IS', 'IT', 'JE', 'LI',
    'LT', 'LU', 'LV', 'MC', 'MD', 'ME', 'MK', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'RS',
    'SE', 'SI', 'SJ', 'SK', 'SM', 'UA', 'VA', 'XK',
])

export const COUNTRY_GROUP_LABELS: Record<
    SupportedCountryLanguage,
    Record<CountryGroupId, string>
> = {
    cs: {
        frequent: 'Nejčastější',
        europe: 'Evropa',
        world: 'Zbytek světa',
    },
    en: {
        frequent: 'Frequent',
        europe: 'Europe',
        world: 'Rest of the world',
    },
    de: {
        frequent: 'Häufig',
        europe: 'Europa',
        world: 'Rest der Welt',
    },
    pl: {
        frequent: 'Najczęstsze',
        europe: 'Europa',
        world: 'Reszta świata',
    },
}

export function normalizeCountryLanguage(language: string): SupportedCountryLanguage {
    const normalizedLanguage = language.toLowerCase().split(/[-_]/)[0]

    if (
        normalizedLanguage === 'cs' ||
        normalizedLanguage === 'en' ||
        normalizedLanguage === 'de' ||
        normalizedLanguage === 'pl'
    ) {
        return normalizedLanguage
    }

    return 'en'
}

export function getCountryFlag(countryCode?: string): string {
    const normalizedCountryCode = countryCode?.trim().toUpperCase()

    if (!normalizedCountryCode || !/^[A-Z]{2}$/.test(normalizedCountryCode)) {
        return FLAG_FALLBACK
    }

    return String.fromCodePoint(
        ...normalizedCountryCode
            .split('')
            .map(letter => 0x1f1e6 + letter.charCodeAt(0) - 65)
    )
}

export function isSupportedInvoiceCountryCode(value: string): boolean {
    const countryCode = value.trim()

    return (
        /^[A-Z]{2}$/.test(countryCode)
        && !UNSUPPORTED_BACKEND_COUNTRIES.has(countryCode)
        && Object.prototype.hasOwnProperty.call(COUNTRIES.en, countryCode)
    )
}

function getCountryGroupId(countryCode: string): CountryGroupId {
    if ((PRIORITY_COUNTRIES as readonly string[]).includes(countryCode)) {
        return 'frequent'
    }

    return EUROPEAN_COUNTRIES.has(countryCode) ? 'europe' : 'world'
}

function getCountryAliases(countryNameOrNames: string | string[]): string[] {
    return Array.isArray(countryNameOrNames) ? countryNameOrNames : [countryNameOrNames]
}

function compareCountryOptions(
    optionA: InvoiceCountryOption,
    optionB: InvoiceCountryOption,
    language: SupportedCountryLanguage,
): number {
    if (optionA.groupId === 'frequent' && optionB.groupId === 'frequent') {
        return (
            PRIORITY_COUNTRIES.indexOf(optionA.isoCode as typeof PRIORITY_COUNTRIES[number])
            - PRIORITY_COUNTRIES.indexOf(optionB.isoCode as typeof PRIORITY_COUNTRIES[number])
        )
    }

    return optionA.name.localeCompare(optionB.name, language, {
        sensitivity: 'base',
    })
}

export function getInvoiceCountryGroups(language: string): InvoiceCountryGroup[] {
    const normalizedLanguage = normalizeCountryLanguage(language)
    const localizedCountries = COUNTRIES[normalizedLanguage]
    const labels = COUNTRY_GROUP_LABELS[normalizedLanguage]
    const options = Object.entries(localizedCountries)
        .filter(([countryCode]) => isSupportedInvoiceCountryCode(countryCode))
        .map(([isoCode, countryNameOrNames]) => {
            const aliases = getCountryAliases(countryNameOrNames)
            const groupId = getCountryGroupId(isoCode)

            return {
                isoCode,
                name: aliases[0],
                aliases,
                groupId,
                groupLabel: labels[groupId],
                searchValue: [...aliases, isoCode].join(' '),
            }
        })

    const groups: InvoiceCountryGroup[] = [
        { id: 'frequent', label: labels.frequent, options: [] },
        { id: 'europe', label: labels.europe, options: [] },
        { id: 'world', label: labels.world, options: [] },
    ]

    for (const option of options) {
        groups.find(group => group.id === option.groupId)?.options.push(option)
    }

    for (const group of groups) {
        group.options.sort((optionA, optionB) =>
            compareCountryOptions(optionA, optionB, normalizedLanguage)
        )
    }

    return groups.filter(group => group.options.length > 0)
}

export function getInvoiceCountryOptions(language: string): InvoiceCountryOption[] {
    return getInvoiceCountryGroups(language).flatMap(group => group.options)
}

export function getInvoiceCountryName(
    countryCode: string,
    language: string,
    fallbackValue = '',
): string {
    if (!isSupportedInvoiceCountryCode(countryCode)) {
        return fallbackValue
    }

    const normalizedLanguage = normalizeCountryLanguage(language)
    const countryNameOrNames = COUNTRIES[normalizedLanguage][countryCode.trim()]

    if (!countryNameOrNames) {
        return fallbackValue
    }

    return getCountryAliases(countryNameOrNames)[0]
}
